import type { ReactNode } from "react"
import { ArrowLeft, Loader2 } from "lucide-react"
import { Button } from "@/shared/components/ui/Button"

interface FormPageLayoutProps {
  title: string
  subtitle?: string
  onCancel: () => void
  onSubmit: () => void
  isSubmitting?: boolean
  submitLabel?: string
  cancelLabel?: string
  children: ReactNode
}

/**
 * Full-page form layout with header, content area, and action footer.
 * Consistent with existing detail page patterns.
 */
export function FormPageLayout({
  title,
  subtitle,
  onCancel,
  onSubmit,
  isSubmitting = false,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  children,
}: FormPageLayoutProps) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-roboto text-2xl font-bold text-gray-900">{title}</h2>
          {subtitle && (
            <p className="font-roboto text-sm text-gray-600 mt-1">{subtitle}</p>
          )}
        </div>
        <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          <ArrowLeft className="h-5 w-5 mr-2" />
          {cancelLabel}
        </Button>
      </div>

      {/* Form Content */}
      <div className="space-y-6">{children}</div>

      {/* Action Footer */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200/50">
        <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          {cancelLabel}
        </Button>
        <Button onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </div>
  )
}
